import { TranslateFunction } from 'react-utilities';
import { AccountPinError, EmailError, PasswordError, PasswordResetError } from './requests';

export const getPersonalizedResources = (translate: TranslateFunction, isUserUnder13: boolean) =>
  ({
    Action: {
      AbortDismissForeverAddEmail: translate('Action.AbortDismissForeverAddEmail'),
      AbortDismissForeverChangePassword: translate('Action.AbortDismissForeverChangePassword'),
      ChangeEmail: translate('Action.ChangeEmail'),
      ConfirmDismissForeverAddEmail: translate('Action.ConfirmDismissForeverAddEmail'),
      ConfirmDismissForeverChangePassword: translate('Action.ConfirmDismissForeverChangePassword'),
      ContinueAddEmail: translate('Action.ContinueAddEmail'),
      ContinueChangePassword: translate('Action.ContinueChangePassword'),
      DismissForever: translate('Action.DismissForever'),
      RemindMeLater: translate('Action.RemindMeLater'),
      ResendPasswordResetEmail: translate('Action.ResendPasswordResetEmail'),
      SecureAccount: translate('Action.SecureAccount'),
      SubmitChangePassword: translate('Action.SubmitChangePassword'),
      UnlockAccountPin: translate('Action.UnlockAccountPin')
    },
    Message: {
      // Some of these messages show up elsewhere in `web-frontend`, but
      // there's no clean or convenient way to re-use them.
      Error: {
        /**
         * From `EmailErrors.cs` in the Account Settings API.
         */
        Email: {
          FeatureDisabled: translate('Message.Error.Email.FeatureDisabled'),
          TooManyAccountsOnEmail: translate('Message.Error.Email.TooManyAccountsOnEmail'),
          TooManyAttemptsToUpdateEmail: translate(
            'Message.Error.Email.TooManyAttemptsToUpdateEmail'
          ),
          InvalidEmailAddress: translate('Message.Error.Email.InvalidEmailAddress'),
          Default: translate('Message.Error.Email.Default')
        },
        Input: {
          InvalidEmail: translate('Message.Error.Input.InvalidEmail'),
          PasswordsDoNotMatch: translate('Message.Error.Input.PasswordsDoNotMatch')
        },
        /**
         * From `PasswordResponseCodes.cs` in the Authentication API.
         */
        Password: {
          Flooded: translate('Message.Error.Password.Flooded'),
          InvalidPassword: translate('Message.Error.Password.InvalidPassword'),
          InvalidCurrentPassword: translate('Message.Error.Password.InvalidCurrentPassword'),
          PinLocked: translate('Message.Error.Password.PinLocked'),
          Default: translate('Message.Error.Password.Default')
        },
        /**
         * From `PasswordResetError.cs` in the Authentication API.
         */
        PasswordReset: {
          UserDoesNotHaveEmail: translate('Message.Error.PasswordReset.UserDoesNotHaveEmail'),
          Default: translate('Message.Error.PasswordReset.Default')
        },
        /**
         * From `AccountPinController.cs` in the Authentication API.
         */
        Pin: {
          NoAccountPin: translate('Message.Error.Pin.NoAccountPin'),
          AccountLocked: translate('Message.Error.Pin.AccountLocked'),
          Flooded: translate('Message.Error.Pin.Flooded'),
          IncorrectPin: translate('Message.Error.Pin.IncorrectPin'),
          Default: translate('Message.Error.Pin.Default')
        }
      }
    },
    Description: {
      AddYourEmail: translate(
        isUserUnder13 ? 'Description.AddYourEmailUnder13' : 'Description.AddYourEmail'
      ),
      AreYouSureDismissForeverAddEmail: translate(
        isUserUnder13
          ? 'Description.AreYouSureDismissForeverAddEmailUnder13'
          : 'Description.AreYouSureDismissForeverAddEmail'
      ),
      AreYouSureDismissForeverChangePassword: translate(
        'Description.AreYouSureDismissForeverChangePassword'
      ),
      ChangeYourPassword: translate('Description.ChangeYourPassword'),
      ChangeYourPasswordSuccess: translate('Description.ChangeYourPasswordSuccess'),
      CheckYourEmail: (emailAddress: string): string =>
        translate(
          isUserUnder13 ? 'Description.CheckYourEmailUnder13' : 'Description.CheckYourEmail',
          { emailAddress: `<b>${emailAddress}</b>` }
        ),
      EnterYourAccountPin: translate('Description.EnterYourAccountPin'),
      UnusualActivity: translate('Description.UnusualActivity')
    },
    Header: {
      AccountPinExpired: translate('Header.AccountPinExpired'),
      AccountPinRequired: translate('Header.AccountPinRequired'),
      AddYourEmail: translate(isUserUnder13 ? 'Header.AddYourEmailUnder13' : 'Header.AddYourEmail'),
      AreYouSure: translate('Header.AreYouSure'),
      ChangeYourPassword: translate('Header.ChangeYourPassword'),
      CheckYourEmail: translate(
        isUserUnder13 ? 'Header.CheckYourEmailUnder13' : 'Header.CheckYourEmail'
      ),
      Success: translate('Header.Success')
    },
    Label: {
      AccountPin: translate('Label.AccountPin'),
      ConfirmNewPassword: translate('Label.ConfirmNewPassword'),
      CurrentPassword: translate('Label.CurrentPassword'),
      MinutesSeconds: (minutes: number, seconds: number): string =>
        translate('Label.MinutesSeconds', {
          minutes,
          seconds
        }),
      NewPassword: translate('Label.NewPassword'),
      TimeRemaining: translate('Label.TimeRemaining'),
      YourEmail: translate(isUserUnder13 ? 'Label.YourEmailUnder13' : 'Label.YourEmail')
    }
  } as const);

export type AccountSecurityPromptResources = ReturnType<typeof getPersonalizedResources>;

export const mapEmailErrorToResource = (
  resources: AccountSecurityPromptResources,
  error: EmailError | null
): string => {
  switch (error) {
    case EmailError.FEATURE_DISABLED:
      return resources.Message.Error.Email.FeatureDisabled;
    case EmailError.TOO_MANY_ACCOUNTS_ON_EMAIL:
      return resources.Message.Error.Email.TooManyAccountsOnEmail;
    case EmailError.TOO_MANY_ATTEMPTS_TO_UPDATE_EMAIL:
      return resources.Message.Error.Email.TooManyAttemptsToUpdateEmail;
    case EmailError.INVALID_EMAIL_ADDRESS:
      return resources.Message.Error.Email.InvalidEmailAddress;
    default:
      return resources.Message.Error.Email.Default;
  }
};

export const mapPasswordErrorToResource = (
  resources: AccountSecurityPromptResources,
  error: PasswordError | null
): string => {
  switch (error) {
    case PasswordError.FLOODED:
      return resources.Message.Error.Password.Flooded;
    case PasswordError.INVALID_PASSWORD:
      return resources.Message.Error.Password.InvalidPassword;
    case PasswordError.INVALID_CURRENT_PASSWORD:
      return resources.Message.Error.Password.InvalidCurrentPassword;
    case PasswordError.PIN_LOCKED:
      return resources.Message.Error.Password.PinLocked;
    default:
      return resources.Message.Error.Password.Default;
  }
};

export const mapPasswordResetErrorToResource = (
  resources: AccountSecurityPromptResources,
  error: PasswordResetError | null
): string => {
  switch (error) {
    case PasswordResetError.USER_DOES_NOT_HAVE_EMAIL:
      return resources.Message.Error.PasswordReset.UserDoesNotHaveEmail;
    default:
      return resources.Message.Error.PasswordReset.Default;
  }
};

export const mapAccountPinErrorCodeToResource = (
  resources: AccountSecurityPromptResources,
  errorCode: AccountPinError | null
): string => {
  switch (errorCode) {
    case AccountPinError.NO_ACCOUNT_PIN:
      return resources.Message.Error.Pin.NoAccountPin;
    case AccountPinError.ACCOUNT_LOCKED:
      return resources.Message.Error.Pin.AccountLocked;
    case AccountPinError.FLOODED:
      return resources.Message.Error.Pin.Flooded;
    case AccountPinError.INCORRECT_PIN:
      return resources.Message.Error.Pin.IncorrectPin;
    default:
      return resources.Message.Error.Pin.Default;
  }
};
